const en = {
  apiErrors: {
    offline:
      'Connection lost. Your unsent draft is still here. Retry the same operation when connected.',
    unauthorized: 'Your session expired. Sign in again before continuing.',
    forbidden: 'Your current session does not have permission for this operation.',
    notFound: 'This item is unavailable or no longer visible.',
    conflict: 'The version changed while you were working. Nothing was overwritten.',
    tooLarge:
      'This change exceeds the vault size limit. Reduce the payload and try again.',
    rateLimited:
      'The vault is temporarily limiting requests. Keep your draft and retry later.',
    unavailable:
      'Writing is temporarily unavailable. Your draft has not been published.',
    invalid: 'Check the document metadata, limits, and required fields.',
    reference: 'Request reference: {{id}}',
  },
  session: {
    local: 'Local development session',
    mock: 'In-memory demo',
    shared: 'Shared vault',
    visitor: 'Read-only visitor',
    signIn: 'Sign in',
    signOut: 'Sign out',
    localToken: 'Local session token',
    localHelp:
      'Paste the token from your local operator setup. It is exchanged for an HttpOnly cookie and is never saved in browser storage.',
    readOnly: 'Vault is read-only',
    role: 'Role: {{role}}',
  },
  editor: {
    proposalId: 'Proposal ID',
    contentHash: 'Reviewed content hash (SHA-256)',
    types: {
      note: 'Note',
      lesson: 'Lesson',
      decision: 'Decision',
      incident: 'Incident',
      process: 'Process',
      document: 'Document',
    },
    loadedCount: '{{count}} loaded documents; more may exist in this branch',
    versionConflict: 'Your document base: v{{base}}. Current document: v{{current}}.',
    proposalConflict: 'Your proposal base: v{{base}}. Current proposal: v{{current}}.',
    metadataFields: {
      type: 'Type',
      folderId: 'Folder',
      tags: 'Tags',
      relations: 'References',
      visibility: 'Visibility',
      status: 'Editorial status',
      locale: 'Language',
    },
    summary: 'Proposal summary',
    metadata: 'Document metadata',
    documentTitle: 'Document title',
    visibility: 'Visibility',
    public: 'Public',
    internal: 'Internal',
    relations: 'Reference IDs',
    commaSeparated: 'Comma-separated values',
    operation: 'Change operation',
    modify: 'Modify document',
    delete: 'Delete document (after merge)',
    editProposal: 'Edit proposal',
    resubmit: 'Save and resubmit',
    proposalVersion: 'Proposal version {{version}}',
    baseVersion: 'Based on document v{{version}}.',
    confirmMerge: 'Confirm merge',
    authorizeMerge: 'Authorize merge',
    mergeExplanation:
      'Publish “{{title}}”, proposal version {{version}}? This confirmation applies only to the version you reviewed.',
    webmcpMergeExplanation:
      'Authorize the agent to merge “{{title}}”, proposal version {{version}}. Nothing is published until the agent explicitly retries the same operation with its original key.',
    mergeConfirmationExpiry:
      'This request expires at {{expiresAt}}. Accepting does not publish; the agent must retry this exact operation.',
    conflictHelp:
      'Your draft is preserved. Open the current document in a new tab, compare versions, then start a new proposal from that revision. The base version is never advanced silently.',
    unsent: 'Unsent draft. Changes here are not saved to the vault.',
    saveLocal: 'Save draft on this device',
    restoreLocal: 'Restore local draft',
    savedLocally: 'Saved on this device only. Not sent to the vault.',
    discard: 'Discard this unsent draft?',
    graphLimited: 'Bounded graph. Narrow the scope to explore more.',
    loadMore: 'Load more',
    loadingFolder: 'Loading folder…',
  },
  brand: { subtitle: 'Living knowledge' },
  nav: {
    atlas: 'Atlas',
    library: 'Library',
    docs: 'Docs',
    proposals: 'Proposals',
    history: 'History',
  },
  shell: {
    openNavigation: 'Open navigation',
    closeNavigation: 'Close navigation',
    skip: 'Skip to content',
    search: 'Search knowledge',
    searchPlaceholder: 'Search decisions, incidents, lessons…',
    newMemory: 'New memory',
    primaryNavigation: 'Primary navigation',
    vault: 'Vault',
    branch: 'branch',
    synced: 'Vault synced',
    momentsAgo: 'moments ago',
  },
  atlas: {
    kicker: 'Knowledge constellation',
    title: 'The living map',
    subtitle: 'Accepted lessons, decisions, and open threads in this vault.',
    relatedTitle: 'Neighborhood of {{title}}',
    relatedSubtitle: 'Direct references and backlinks around the selected document.',
    folderSubtitle:
      'Folder knowledge stays bright while outside relations remain visible.',
    related: 'Related',
    folder: 'Folder',
    entire: 'Entire vault',
    listView: 'List view',
    graphView: 'Graph view',
    legendKnowledge: 'Knowledge',
    legendDecision: 'Decision',
    legendIncident: 'Incident',
    legendLabel: 'Graph legend',
    graphLabel: 'Knowledge graph',
    nodes: '{{count}} memories',
    relations: '{{count}} relations',
    openNode: 'Open {{title}}',
    noGraph: 'No graph data is available for this scope.',
    external: 'External relation',
    interactionHint:
      'Select a memory to reveal its neighborhood. Double-click to open.',
    selectedHint: 'Neighborhood highlighted. Use the arrow on the node to open it.',
    selectNode: 'Select {{title}}',
    galaxies: '{{count}} galaxies',
    bridges: '{{count}} bridges',
    hub: 'Knowledge hub',
    cameraControls: 'Camera controls',
    panMap: 'Pan map',
    rotateLeft: 'Rotate left',
    rotateRight: 'Rotate right',
    tiltUp: 'Tilt up',
    tiltDown: 'Tilt down',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    resetView: 'Reset view',
    pauseMotion: 'Pause motion',
    reducedMotion: 'Motion disabled by your system preference',
    resumeMotion: 'Resume motion',
    cameraHint: 'Drag to orbit · Right/Shift + drag to pan · Scroll to zoom',
    cameraPanHint: 'Drag to pan · Turn off Pan map to orbit · Scroll to zoom',
    cameraKeyboardHint: 'Arrows rotate · Shift + arrows pan · Home resets',
    canvasUnavailable:
      'The spatial view is unavailable. Choose List view to explore every memory.',
  },
  folders: {
    title: 'Folders',
    all: 'All knowledge',
    collapse: 'Collapse {{name}}',
    expand: 'Expand {{name}}',
    openFolder: 'Open {{name}}',
    documentCount_one: '{{count}} document',
    documentCount_other: '{{count}} documents',
    documents: '{{count}} documents',
  },
  library: {
    kicker: 'Markdown library',
    title: 'All knowledge',
    lead: 'Browse the durable source behind the constellation. Every document remains portable Markdown.',
    reset: 'All documents',
    viewAtlas: 'View as constellation',
    search: 'Filter documents',
    searchPlaceholder: 'Filter by title, path, tag…',
    sort: 'Sort',
    sortUpdated: 'Recently updated',
    sortTitle: 'Title',
    sortKind: 'Type',
    list: 'List',
    cards: 'Cards',
    view: 'View',
    results: '{{count}} documents',
    relationsCount: '{{count}} relations',
    emptyTitle: 'No documents match these filters',
    emptyBody: 'Try clearing the folder or search filter.',
    path: 'Path',
    type: 'Type',
    status: 'Status',
    author: 'Author',
    updated: 'Updated',
    relations: 'Relations',
  },
  document: {
    preview: 'Preview',
    markdown: 'Markdown',
    relations: 'Relations',
    history: 'History',
    graph: 'Graph',
    document: 'Document',
    edit: 'Edit',
    cancel: 'Cancel',
    propose: 'Propose changes',
    proposeChanges: 'Propose changes',
    copyLink: 'Copy link',
    copied: 'Knowledge link copied',
    contributors: 'Contributors',
    details: 'Details',
    version: 'Version',
    linksOut: 'Links out',
    backlinks: 'Backlinks',
    tags: 'Tags',
    noRelations: 'No relations yet.',
    relationsLead: 'Follow the links that give this memory context.',
    sourceNote:
      'Editing a proposal draft. Published knowledge changes only after merge.',
    notFound: 'Document not found',
    backLibrary: 'Back to Library',
    openDocument: 'Open document {{title}}',
    revision: 'Revision {{version}}',
    currentVersion: 'Current version',
    created: 'Created',
  },
  proposals: {
    kicker: 'Vault governance',
    title: 'Proposals',
    lead: 'Knowledge becomes official through visible review—not silent edits.',
    all: 'All',
    open: 'Open',
    changesRequested: 'Changes requested',
    approved: 'Approved',
    merged: 'Merged',
    awaiting: '{{count}} awaiting resolution',
    emptyTitle: 'No proposals in this state',
    emptyBody: 'Choose another status or return to all proposals.',
    listLabel: 'Proposal list',
    detailLabel: 'Proposal detail',
    detailPrompt:
      'Open a proposal to inspect its checks, discussion, and exact Markdown diff.',
    files: '{{count}} files',
    checks: '{{passed}}/{{total}} checks passed',
    linksResolved: 'links resolved',
    formatValid: 'format valid',
    approve: 'Approve',
    merge: 'Merge into vault',
    requestChanges: 'Request changes',
    mergeReason: 'Merge creates the next published revision.',
    back: 'Back to proposals',
    affectedFiles: 'Affected files',
    reviewSummary: 'Review summary',
    openProposal: 'Open proposal #{{number}}',
    noSelection: 'Select a proposal to review.',
    newProposal: 'New proposal',
    titleField: 'Proposal title',
    reasonField: 'Review note',
    reasonPlaceholder: 'Explain the intent or the changes required…',
    noDiff: 'No textual diff is available.',
    newFile: 'New file',
    pathUnavailable: 'Path unavailable',
    reviewQueue: 'Review queue',
    queueSummary: '{{count}} proposals in this view',
    queuePolicy: 'main · review required',
    newDocument: 'New document',
    updatedBy: 'updated {{date}} by {{author}}',
    changeCount_one: '{{count}} changed file',
    changeCount_other: '{{count}} changed files',
  },
  history: {
    kicker: 'Immutable trail',
    title: 'Vault history',
    lead: 'Every accepted change leaves a readable trail of who changed what, why, and which version resulted.',
    export: 'Export log',
    versions: '{{branch}} · {{count}} versions',
    openEvent: 'Open history event {{title}}',
    proposal: 'Proposal',
    document: 'Document',
    revision: 'Revision',
    noEvents: 'No history events yet.',
    eventDetail: 'History event',
    back: 'Back to history',
    resultingRevision: 'Resulting revision',
    publishedRevision: 'Published as revision {{version}}.',
    immutable: 'This event is part of the immutable vault trail.',
    searchPlaceholder: 'Search actor, change, or document…',
    eventType: 'Event type',
    eventTypes: {
      proposal: 'Proposal',
      publish: 'Publish',
      restore: 'Restore',
      relation: 'Relation',
      create: 'Create',
    },
  },
  docs: {
    kicker: 'Vault docs',
    title: 'Learn Lorestra',
    lead: 'Product documentation lives in the same reviewable vault as engineering knowledge.',
    read: 'Read guide',
    cookbook: 'Cookbook',
    whatIs: 'What is Lorestra?',
    usage: 'Usage guide',
    noDoc: 'This document is not available in the selected language.',
    back: 'Back to Docs',
    contents: 'Contents',
    agentReady: 'WebMCP-native knowledge',
    agentReadyBody:
      'People get a calm bilingual workspace. Agents get eleven typed tools for discovery, reading, graph context, proposals, and history.',
  },
  common: {
    loading: 'Loading…',
    retry: 'Try again',
    errorTitle: 'Something went wrong',
    unknown: 'Unknown',
    justNow: 'Just now',
    locale: 'Language',
    close: 'Close',
    open: 'Open',
    clear: 'Clear',
    selected: 'Selected',
    status: 'Status',
    search: 'Search',
    required: 'This field is required.',
    pagination: 'Pagination',
    paginationRange: '{{start}}–{{end}} of {{total}}',
    paginationTotal: '{{total}} total',
    previous: 'Previous',
    next: 'Next',
    published: 'Published',
    draft: 'Draft',
    archived: 'Archived',
    kind: {
      incident: 'Incident',
      decision: 'Decision',
      runbook: 'Runbook',
      guide: 'Guide',
      process: 'Process',
      note: 'Note',
      docs: 'Docs',
      folder: 'Folder',
    },
  },
}

export default en
